import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

export interface Plan {
  id: string;
  name: string;
  max_organizations: number;
  max_workspaces: number;
  max_boards_per_workspace: number;
  max_pomodoros_per_workspace: number;
  can_add_collaborators: boolean;
  can_share_with_external_users: boolean;
}

export interface UserPlan {
  plan: Plan;
  isLoading: boolean;
  error: Error | null;
  canCreateWorkspace: () => Promise<boolean>;
  canCreateBoard: (workspaceId: string) => Promise<boolean>;
  canAddCollaborators: () => boolean;
  canShareWithExternalUsers: () => boolean;
}

export const useSubscription = (): UserPlan => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { data: plan, isLoading, error } = useQuery({
    queryKey: ["userPlan"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not found");

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("plan_id, plans(*)")
        .eq("id", user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.plans) throw new Error("Plan not found");

      return profile.plans as Plan;
    },
  });

  const canCreateWorkspace = async () => {
    if (!plan) return false;

    const { data: workspaces, error: countError } = await supabase
      .from("workspaces")
      .select("id", { count: "exact" });

    if (countError) {
      toast({
        title: t("common.error"),
        description: t("common.errorOccurred"),
        variant: "destructive",
      });
      return false;
    }

    const currentCount = workspaces?.length || 0;
    const canCreate = currentCount < plan.max_workspaces;

    if (!canCreate) {
      toast({
        title: t("common.limitReached"),
        description: t("common.upgradeToCreateMore"),
        variant: "destructive",
      });
    }

    return canCreate;
  };

  const canCreateBoard = async (workspaceId: string) => {
    if (!plan) return false;

    const { data: boards, error: countError } = await supabase
      .from("boards")
      .select("id")
      .eq("workspace_id", workspaceId);

    if (countError) {
      toast({
        title: t("common.error"),
        description: t("common.errorOccurred"),
        variant: "destructive",
      });
      return false;
    }

    const currentCount = boards?.length || 0;
    const canCreate = currentCount < plan.max_boards_per_workspace;

    if (!canCreate) {
      toast({
        title: t("common.limitReached"),
        description: t("common.upgradeToCreateMore"),
        variant: "destructive",
      });
    }

    return canCreate;
  };

  const canAddCollaborators = () => {
    return plan?.can_add_collaborators || false;
  };

  const canShareWithExternalUsers = () => {
    return plan?.can_share_with_external_users || false;
  };

  return {
    plan: plan as Plan,
    isLoading,
    error: error as Error | null,
    canCreateWorkspace,
    canCreateBoard,
    canAddCollaborators,
    canShareWithExternalUsers,
  };
};